import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { 
  collection, 
  doc, 
  getDoc,
  writeBatch,
  serverTimestamp 
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage, auth } from "../firebase";
import { Alert, Button, Form, Container, Spinner, Card, Row, Col } from "react-bootstrap";
import { useAuthState } from "react-firebase-hooks/auth";
import { sendRequestCreatedEmails } from "../services/emailNotifications";

const RequestMedication = () => {
  const [formData, setFormData] = useState({
    notes: "",
    prescriptionFile: null,
    prescriptionUrl: null
  });
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: "", variant: "success" });
  const [medication, setMedication] = useState(null);
  const { medicationId } = useParams();
  const [user, userLoading] = useAuthState(auth);
  const navigate = useNavigate();
  const [fileError, setFileError] = useState("");
  const [filePreview, setFilePreview] = useState(null);

  // Obtener información del medicamento
  useEffect(() => {
    const fetchMedication = async () => {
      try {
        const medicationRef = doc(db, "medications", medicationId);
        const medicationSnap = await getDoc(medicationRef);

        if (!medicationSnap.exists()) {
          throw new Error("Medicamento no encontrado");
        }

        setMedication({ id: medicationSnap.id, ...medicationSnap.data() });
      } catch (error) {
        setAlert({
          show: true,
          message: error.message || "Error al cargar el medicamento",
          variant: "danger"
        });
        setTimeout(() => navigate("/"), 2000);
      }
    };

    fetchMedication();
  }, [medicationId, navigate]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validaciones del archivo
    const validTypes = ["image/jpeg", "image/png", "application/pdf"];
    const maxSize = 5 * 1024 * 1024; // 5MB
    
    if (!validTypes.includes(file.type)) {
      setFileError("Formato no válido. Sube JPG, PNG o PDF.");
      return;
    }
    
    if (file.size > maxSize) {
      setFileError("El archivo es demasiado grande (máximo 5MB)");
      return;
    }

    setFileError("");
    setFormData(prev => ({
      ...prev,
      prescriptionFile: file
    }));

    // Mostrar vista previa si es imagen
    if (file.type.includes("image")) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFilePreview(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      setFilePreview(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setAlert({ ...alert, show: false });

    if (!user || !medication) {
      setAlert({
        show: true,
        message: !user ? "Debes iniciar sesión" : "Medicamento no encontrado",
        variant: "danger"
      });
      setLoading(false);
      return;
    }

    try {
      let prescriptionUrl = formData.prescriptionUrl;
      
      // Subir archivo si se seleccionó uno nuevo
      if (formData.prescriptionFile) {
        const fileRef = ref(
          storage, 
          `prescriptions/${user.uid}/${Date.now()}_${formData.prescriptionFile.name}`
        );
        await uploadBytes(fileRef, formData.prescriptionFile);
        prescriptionUrl = await getDownloadURL(fileRef);
      }

      // Crear datos de la solicitud
      const requestData = {
        medicationId: medication.id,
        medicationName: medication.name,
        medicationDosage: medication.dosage,
        medicationImageUrl: medication.imageUrl,
        medicationCategory: medication.category,
        medicationCondition: medication.condition,
        medicationQuantity: medication.quantity,
        medicationExpirationDate: medication.expirationDate,
        
        donorId: medication.donorId,
        donorEmail: medication.donorEmail,
        
        userId: user.uid,
        userEmail: user.email,
        userName: user.displayName || user.email.split('@')[0],
        requesterNotes: formData.notes,
        
        prescriptionUrl,
        prescriptionName: formData.prescriptionFile?.name || null,
        status: "PENDIENTE",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      // Operación atómica con batch
      const batch = writeBatch(db);

      // Crear en colección principal
      const requestRef = doc(collection(db, "requests"));
      batch.set(requestRef, requestData);

      // Crear en subcolección del usuario
      const userRequestRef = doc(db, `users/${user.uid}/requests`, requestRef.id);
      batch.set(userRequestRef, requestData);

      // Actualizar disponibilidad del medicamento
      const medicationRef = doc(db, "medications", medication.id);
      batch.update(medicationRef, {
        quantity: medication.quantity - 1
      });

      await batch.commit();

      await sendRequestCreatedEmails({
        requestId: requestRef.id,
        medicationName: medication.name,
        requesterEmail: user.email,
        requesterName: user.displayName || user.email?.split("@")?.[0],
        donorEmail: medication.donorEmail,
        donorName: medication.donorName,
      });

      setAlert({
        show: true,
        message: "¡Solicitud creada correctamente!",
        variant: "success"
      });
      setTimeout(() => navigate("/mis-solicitudes"), 1500);
    } catch (error) {
      console.error("Error:", error);
      setAlert({
        show: true,
        message: error.message || "Error al procesar la solicitud",
        variant: "danger"
      });
    } finally {
      setLoading(false);
    }
  };

  if (userLoading || !medication) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2">Cargando información...</p>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <h1 className="text-center mb-4">Solicitar Medicamento</h1>
      
      {alert.show && (
        <Alert variant={alert.variant} onClose={() => setAlert({...alert, show: false})} dismissible>
          {alert.message}
        </Alert>
      )}

      <Card className="mb-4 shadow-sm">
        <Card.Header className="bg-light">
          <h5 className="mb-0">Detalles del Medicamento</h5>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={3}>
              {medication.imageUrl && (
                <img 
                  src={medication.imageUrl} 
                  alt={medication.name} 
                  className="img-fluid rounded mb-3"
                  style={{ maxHeight: "150px", objectFit: "cover" }}
                />
              )}
            </Col>
            <Col md={9}>
              <h4>{medication.name}</h4>
              <p><strong>Dosis:</strong> {medication.dosage}</p>
              <p><strong>Categoría:</strong> {medication.category}</p>
              <p><strong>Condición:</strong> {medication.condition}</p>
              <p><strong>Cantidad disponible:</strong> {medication.quantity}</p>
              <p><strong>Fecha de expiración:</strong> {new Date(medication.expirationDate).toLocaleDateString()}</p>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Form onSubmit={handleSubmit} className="border p-4 rounded-3 shadow-sm">
        <Form.Group className="mb-3">
          <Form.Label>Notas adicionales</Form.Label>
          <Form.Control
            as="textarea"
            name="notes"
            value={formData.notes}
            onChange={(e) => setFormData({...formData, notes: e.target.value})}
            rows={3}
            placeholder="Información adicional para el donante"
          />
        </Form.Group>

        <Form.Group className="mb-4">
          <Form.Label>Receta Médica (Opcional pero recomendado)</Form.Label>
          <Form.Control
            type="file"
            onChange={handleFileChange}
            accept=".jpg,.jpeg,.png,.pdf"
            isInvalid={!!fileError}
          />
          <Form.Text className="text-muted">
            Formatos aceptados: JPG, PNG, PDF (Máximo 5MB)
          </Form.Text>
          {fileError && (
            <Form.Control.Feedback type="invalid">
              {fileError}
            </Form.Control.Feedback>
          )}
          
          {/* Vista previa de la imagen */}
          {filePreview && (
            <div className="mt-3">
              <p className="mb-2"><strong>Vista previa:</strong></p>
              <img 
                src={filePreview} 
                alt="Vista previa de la receta" 
                className="img-thumbnail"
                style={{ maxHeight: "200px" }}
              />
            </div>
          )}
        </Form.Group>

        <div className="d-grid gap-2">
          <Button
            variant="primary"
            type="submit"
            disabled={loading}
            size="lg"
          >
            {loading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Procesando...
              </>
            ) : (
              "Confirmar Solicitud"
            )}
          </Button>
          <Button
            variant="outline-secondary"
            onClick={() => navigate(-1)}
            disabled={loading}
          >
            Cancelar
          </Button>
        </div>
      </Form>
    </Container>
  );
};

export default RequestMedication;
